import React, { useState } from 'react';
import {
  TraverseIcon,
  TraverseTyreReferenceIcon,
  TraverseTyreComparisonIcon,
  TraverseIndoorsIcon,
  TraverseCatesbyIcon,
  LoopIcon,
  LoopVelodromeOutdoorsIcon,
  LoopVelodromeIndoorsIcon,
  FreerideCalibratedIcon,
  CalipersIcon,
  TunnelIcon,
  TyreOvalIcon,
} from '../../icons/AnalysisTypeIcons';
import { PencilIcon } from '../../icons/PencilIcon';
import { StreamlinesAdmin } from '../../icons/StreamlinesAdmin';
import { StreamlinesAdvanced } from '../../icons/StreamlinesAdvanced';
import { StreamlinesBicycle } from '../../icons/StreamlinesBicycle';
import { StreamlinesCalibration } from '../../icons/StreamlinesCalibration';
import { StreamlinesReports } from '../../icons/StreamlinesReports';
import { StreamlinesRider } from '../../icons/StreamlinesRider';
import { StreamlinesRoute } from '../../icons/StreamlinesRoute';
import { StreamlinesRun } from '../../icons/StreamlinesRun';
import { StreamlinesSession } from '../../icons/StreamlinesSession';
import { StreamlinesUser } from '../../icons/StreamlinesUser';
import { VelodromeOval, VelodromeIndoorIcon, VelodromeOutdoorIcon } from '../../icons/VelodromeIcons';
import { getColors, getFontFamilies, getScreens } from '../../utils/tailwindConfigReader';
import { useForm, FormProvider } from 'react-hook-form';
import Button from '../../components/Button';
import ButtonList from '../../components/ButtonList';
import FormInput from '../../components/FormInput';
import FormEntry from '../../components/FormEntry';
import { HiOutlineCheckCircle, HiOutlineXMark, HiOutlineInformationCircle } from 'react-icons/hi2';

type TabType = 'icons' | 'colors' | 'typography' | 'buttons' | 'forms';

// =============================================================================
// Tab Navigation
// =============================================================================
const TabButton: React.FC<{
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}> = ({ active, onClick, children }) => (
  <button
    onClick={onClick}
    className={`cr-px-6 cr-py-3 cr-text-sm cr-font-semibold cr-transition-all cr-duration-200 cr-border-b-2 ${
      active
        ? 'cr-border-blue-500 cr-text-blue-600 cr-bg-blue-50'
        : 'cr-border-transparent cr-text-gray-600 hover:cr-text-gray-900 hover:cr-bg-gray-50'
    }`}
  >
    {children}
  </button>
);

// =============================================================================
// Icons Tab Components
// =============================================================================
interface IconDisplayProps {
  name: string;
  component: React.FC<any>;
  description?: string;
  props?: string;
  renderType: 'size' | 'svg' | 'streamlines';
}

const IconDisplay: React.FC<IconDisplayProps> = ({ name, component: Icon, description, props, renderType }) => {
  const renderIcon = () => {
    switch (renderType) {
      case 'size':
        return <Icon size={32} className="cr-text-gray-700" />;
      case 'svg':
        return <Icon className="cr-w-8 cr-h-8 cr-text-gray-700" />;
      case 'streamlines':
        return (
          <div className="cr-w-8 cr-h-8 cr-text-gray-700">
            <Icon fill="currentColor" stroke="currentColor" />
          </div>
        );
      default:
        return <Icon size={32} className="cr-text-gray-700" />;
    }
  };

  return (
    <div className="cr-flex cr-flex-col cr-items-center cr-gap-2 cr-p-4 cr-bg-white cr-rounded-lg cr-border cr-border-gray-200 hover:cr-border-blue-400 hover:cr-shadow-md cr-transition-all">
      <div className="cr-flex cr-items-center cr-justify-center cr-w-16 cr-h-16 cr-bg-gray-50 cr-rounded-lg">
        {renderIcon()}
      </div>
      <div className="cr-text-center">
        <h3 className="cr-font-mono cr-text-xs cr-font-semibold cr-text-gray-900 cr-break-all">{name}</h3>
        {description && <p className="cr-text-2xs cr-text-gray-600 cr-mt-1">{description}</p>}
        {props && <p className="cr-text-2xs cr-text-blue-600 cr-mt-1 cr-font-mono">{props}</p>}
      </div>
    </div>
  );
};

const IconsTab: React.FC = () => {
  const analysisTypeIcons: IconDisplayProps[] = [
    { name: 'TraverseIcon', component: TraverseIcon, description: 'Basic traverse session', props: 'size, className', renderType: 'size' },
    { name: 'TraverseTyreReferenceIcon', component: TraverseTyreReferenceIcon, description: 'Traverse with tyre reference', props: 'size, className', renderType: 'size' },
    { name: 'TraverseTyreComparisonIcon', component: TraverseTyreComparisonIcon, description: 'Traverse with tyre comparison', props: 'size, className', renderType: 'size' },
    { name: 'TraverseIndoorsIcon', component: TraverseIndoorsIcon, description: 'Indoor traverse session', props: 'size, className', renderType: 'size' },
    { name: 'TraverseCatesbyIcon', component: TraverseCatesbyIcon, description: 'Catesby traverse session', props: 'size, className', renderType: 'size' },
    { name: 'LoopIcon', component: LoopIcon, description: 'Basic loop session', props: 'size, className', renderType: 'size' },
    { name: 'LoopVelodromeOutdoorsIcon', component: LoopVelodromeOutdoorsIcon, description: 'Outdoor velodrome loop', props: 'size, className', renderType: 'size' },
    { name: 'LoopVelodromeIndoorsIcon', component: LoopVelodromeIndoorsIcon, description: 'Indoor velodrome loop', props: 'size, className', renderType: 'size' },
    { name: 'FreerideCalibratedIcon', component: FreerideCalibratedIcon, description: 'Calibrated freeride', props: 'size, className', renderType: 'size' },
    { name: 'CalipersIcon', component: CalipersIcon, description: 'Calibration tool icon', props: 'SVG props', renderType: 'svg' },
    { name: 'TunnelIcon', component: TunnelIcon, description: 'Tunnel/Catesby icon', props: 'SVG props', renderType: 'svg' },
    { name: 'TyreOvalIcon', component: TyreOvalIcon, description: 'Tyre oval badge', props: 'SVG props', renderType: 'svg' },
  ];

  const streamlinesIcons: IconDisplayProps[] = [
    { name: 'StreamlinesSession', component: StreamlinesSession, description: 'Sessions navigation', props: 'fill, stroke', renderType: 'streamlines' },
    { name: 'StreamlinesRun', component: StreamlinesRun, description: 'Runs navigation', props: 'fill, stroke', renderType: 'streamlines' },
    { name: 'StreamlinesBicycle', component: StreamlinesBicycle, description: 'Equipment/Bicycle', props: 'fill, stroke', renderType: 'streamlines' },
    { name: 'StreamlinesRider', component: StreamlinesRider, description: 'Rider/Team', props: 'fill, stroke', renderType: 'streamlines' },
    { name: 'StreamlinesUser', component: StreamlinesUser, description: 'User/Account', props: 'fill, stroke', renderType: 'streamlines' },
    { name: 'StreamlinesAdmin', component: StreamlinesAdmin, description: 'Admin', props: 'fill, stroke', renderType: 'streamlines' },
    { name: 'StreamlinesAdvanced', component: StreamlinesAdvanced, description: 'Advanced settings', props: 'fill, stroke', renderType: 'streamlines' },
    { name: 'StreamlinesRoute', component: StreamlinesRoute, description: 'Route navigation', props: 'fill, stroke', renderType: 'streamlines' },
    { name: 'StreamlinesReports', component: StreamlinesReports, description: 'Reports/Analytics', props: 'fill, stroke', renderType: 'streamlines' },
    { name: 'StreamlinesCalibration', component: StreamlinesCalibration, description: 'Calibration', props: 'fill, stroke', renderType: 'streamlines' },
  ];

  const utilityIcons: IconDisplayProps[] = [
    { name: 'PencilIcon', component: PencilIcon, description: 'Edit/Pencil icon', props: 'className, size', renderType: 'size' },
  ];

  const velodromeIcons: IconDisplayProps[] = [
    { name: 'VelodromeOval', component: VelodromeOval, description: 'Core tilted oval track', props: 'className', renderType: 'svg' },
    { name: 'VelodromeIndoorIcon', component: VelodromeIndoorIcon, description: 'Indoor velodrome with V-roof', props: 'className', renderType: 'svg' },
    { name: 'VelodromeOutdoorIcon', component: VelodromeOutdoorIcon, description: 'Outdoor velodrome with sun', props: 'className', renderType: 'svg' },
  ];

  return (
    <div className="cr-space-y-8">
      <section>
        <h2 className="cr-text-xl cr-font-bold cr-text-gray-900 cr-mb-2 cr-pb-2 cr-border-b-2 cr-border-gray-200">
          Analysis Type Icons
        </h2>
        <p className="cr-text-sm cr-text-gray-600 cr-mb-4">
          Session type icons using HeroIcons and custom SVG components for traverse, loop, and freeride sessions.
        </p>
        <div className="cr-grid cr-grid-cols-3 sm:cr-grid-cols-4 md:cr-grid-cols-5 lg:cr-grid-cols-6 cr-gap-3">
          {analysisTypeIcons.map((iconData) => (
            <IconDisplay key={iconData.name} {...iconData} />
          ))}
        </div>
      </section>

      <section>
        <h2 className="cr-text-xl cr-font-bold cr-text-gray-900 cr-mb-2 cr-pb-2 cr-border-b-2 cr-border-gray-200">
          Streamlines Navigation Icons
        </h2>
        <p className="cr-text-sm cr-text-gray-600 cr-mb-4">
          Custom navigation icons for the main application menu.
        </p>
        <div className="cr-grid cr-grid-cols-3 sm:cr-grid-cols-4 md:cr-grid-cols-5 lg:cr-grid-cols-6 cr-gap-3">
          {streamlinesIcons.map((iconData) => (
            <IconDisplay key={iconData.name} {...iconData} />
          ))}
        </div>
      </section>

      <section>
        <h2 className="cr-text-xl cr-font-bold cr-text-gray-900 cr-mb-2 cr-pb-2 cr-border-b-2 cr-border-gray-200">
          Utility Icons
        </h2>
        <div className="cr-grid cr-grid-cols-3 sm:cr-grid-cols-4 md:cr-grid-cols-5 lg:cr-grid-cols-6 cr-gap-3">
          {utilityIcons.map((iconData) => (
            <IconDisplay key={iconData.name} {...iconData} />
          ))}
        </div>
      </section>

      <section>
        <h2 className="cr-text-xl cr-font-bold cr-text-gray-900 cr-mb-2 cr-pb-2 cr-border-b-2 cr-border-gray-200">
          Velodrome Icons
        </h2>
        <p className="cr-text-sm cr-text-gray-600 cr-mb-4">
          Clean React + Tailwind-friendly velodrome icons with currentColor inheritance.
        </p>
        <div className="cr-grid cr-grid-cols-3 sm:cr-grid-cols-4 md:cr-grid-cols-5 lg:cr-grid-cols-6 cr-gap-3">
          {velodromeIcons.map((iconData) => (
            <IconDisplay key={iconData.name} {...iconData} />
          ))}
        </div>
      </section>

      <section>
        <h2 className="cr-text-xl cr-font-bold cr-text-gray-900 cr-mb-4 cr-pb-2 cr-border-b-2 cr-border-gray-200">
          Usage Examples
        </h2>
        <div className="cr-bg-white cr-rounded-lg cr-border cr-border-gray-200 cr-p-6 cr-space-y-6">
          <div>
            <h3 className="cr-font-semibold cr-text-gray-900 cr-mb-2">Analysis Type Icons</h3>
            <pre className="cr-bg-gray-900 cr-text-gray-100 cr-p-4 cr-rounded-lg cr-overflow-x-auto cr-text-sm">
{`import { TraverseIcon } from '@/icons/AnalysisTypeIcons';

<TraverseIcon size={24} className="text-blue-600" />`}
            </pre>
          </div>
          <div>
            <h3 className="cr-font-semibold cr-text-gray-900 cr-mb-2">Streamlines Navigation Icons</h3>
            <pre className="cr-bg-gray-900 cr-text-gray-100 cr-p-4 cr-rounded-lg cr-overflow-x-auto cr-text-sm">
{`import { StreamlinesSession } from '@/icons/StreamlinesSession';

<StreamlinesSession fill="currentColor" stroke="currentColor" />`}
            </pre>
          </div>
          <div>
            <h3 className="cr-font-semibold cr-text-gray-900 cr-mb-2">Velodrome Icons</h3>
            <pre className="cr-bg-gray-900 cr-text-gray-100 cr-p-4 cr-rounded-lg cr-overflow-x-auto cr-text-sm">
{`import { VelodromeIndoorIcon } from '@/icons/VelodromeIcons';

<VelodromeIndoorIcon className="w-8 h-8 text-gray-800" />`}
            </pre>
          </div>
        </div>
      </section>
    </div>
  );
};

// =============================================================================
// Colors Tab
// =============================================================================
interface ColorSwatchProps {
  name: string;
  shades: Record<string, string> | string;
}

const ColorSwatch: React.FC<ColorSwatchProps> = ({ name, shades }) => {
  if (typeof shades === 'string') {
    return (
      <div className="cr-flex cr-flex-col cr-gap-2">
        <div
          className="cr-w-full cr-h-16 cr-rounded-lg cr-border cr-border-gray-200 cr-shadow-sm"
          style={{ backgroundColor: shades }}
        />
        <div className="cr-text-center">
          <p className="cr-font-semibold cr-text-sm cr-text-gray-900">{name}</p>
          <p className="cr-text-xs cr-font-mono cr-text-gray-600">{shades}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="cr-space-y-2">
      <h3 className="cr-font-semibold cr-text-gray-900 cr-capitalize">{name}</h3>
      <div className="cr-grid cr-grid-cols-4 sm:cr-grid-cols-6 md:cr-grid-cols-8 cr-gap-2">
        {Object.entries(shades).map(([shade, value]) => (
          <div key={shade} className="cr-flex cr-flex-col cr-items-center cr-gap-1">
            <div
              className="cr-w-full cr-h-12 cr-rounded-md cr-border cr-border-gray-200 cr-shadow-sm"
              style={{ backgroundColor: value }}
            />
            <p className="cr-text-xs cr-font-medium cr-text-gray-700">{shade}</p>
            <p className="cr-text-2xs cr-font-mono cr-text-gray-500">{value}</p>
          </div>
        ))}
      </div>
    </div>
  );
};

const ColorsTab: React.FC = () => {
  const colors = getColors();

  // SCSS brand colors (not in Tailwind config)
  const scssColors = {
    'Brand Blues': {
      aeroBlue: '#37d8fa',
      aeroTextBlue: '#4cdffb',
      aeroDarkBlue: '#1879b9',
      aeroNavyBlue: '#0f5a86',
    },
    'Brand Greens': {
      aeroGreen: '#30b466',
      aeroTextGreen: '#40b465',
      aeroDarkGreen: '#228a76',
      aeroNavyGreen: '#176f80',
    },
    'Brand Neutrals': {
      aeroDark: '#0a0a0a',
      aeroGrey: '#f2f2f2',
      aeroDarkGrey: '#333333',
      aeroTextDarkGrey: '#545454',
      aeroTextGrey: '#c1c1c1',
    },
    'Brand Accents': {
      jumboYellow: '#fbbd1a',
      aeroRed: '#dc3545',
      aeroDarkRed: '#a71d2a',
    },
  };

  // Separate colors by type
  const colorPalettes = Object.entries(colors).filter(([, value]) => typeof value === 'object');
  const singleColors = Object.entries(colors).filter(([, value]) => typeof value === 'string' && value !== 'transparent');

  return (
    <div className="cr-space-y-8">
      <section>
        <h2 className="cr-text-xl cr-font-bold cr-text-gray-900 cr-mb-4 cr-pb-2 cr-border-b-2 cr-border-gray-200">
          Tailwind Color Palettes
        </h2>
        <p className="cr-text-sm cr-text-gray-600 cr-mb-6">
          Color palettes defined in <code className="cr-bg-gray-100 cr-px-1 cr-rounded">tailwind.config.cjs</code>.
          Use with <code className="cr-bg-gray-100 cr-px-1 cr-rounded">cr-</code> prefix (e.g., <code className="cr-bg-gray-100 cr-px-1 cr-rounded">cr-bg-blue-500</code>).
        </p>
        <div className="cr-space-y-6">
          {colorPalettes.map(([name, shades]) => (
            <ColorSwatch key={name} name={name} shades={shades as Record<string, string>} />
          ))}
        </div>
      </section>

      <section>
        <h2 className="cr-text-xl cr-font-bold cr-text-gray-900 cr-mb-4 cr-pb-2 cr-border-b-2 cr-border-gray-200">
          Single Colors
        </h2>
        <div className="cr-grid cr-grid-cols-3 sm:cr-grid-cols-4 md:cr-grid-cols-6 cr-gap-4">
          {singleColors.map(([name, value]) => (
            <ColorSwatch key={name} name={name} shades={value as string} />
          ))}
        </div>
      </section>

      <section>
        <h2 className="cr-text-xl cr-font-bold cr-text-gray-900 cr-mb-4 cr-pb-2 cr-border-b-2 cr-border-gray-200">
          SCSS Brand Colors
        </h2>
        <p className="cr-text-sm cr-text-gray-600 cr-mb-6">
          Legacy SCSS variables defined in <code className="cr-bg-gray-100 cr-px-1 cr-rounded">index.scss</code>.
        </p>
        <div className="cr-space-y-6">
          {Object.entries(scssColors).map(([category, colors]) => (
            <div key={category}>
              <h3 className="cr-font-semibold cr-text-gray-900 cr-mb-3">{category}</h3>
              <div className="cr-grid cr-grid-cols-2 sm:cr-grid-cols-4 md:cr-grid-cols-6 cr-gap-4">
                {Object.entries(colors).map(([name, value]) => (
                  <div key={name} className="cr-flex cr-flex-col cr-gap-2">
                    <div
                      className="cr-w-full cr-h-16 cr-rounded-lg cr-border cr-border-gray-200 cr-shadow-sm"
                      style={{ backgroundColor: value }}
                    />
                    <div className="cr-text-center">
                      <p className="cr-font-medium cr-text-xs cr-text-gray-900">${name}</p>
                      <p className="cr-text-2xs cr-font-mono cr-text-gray-600">{value}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </section>

      <section>
        <h2 className="cr-text-xl cr-font-bold cr-text-gray-900 cr-mb-4 cr-pb-2 cr-border-b-2 cr-border-gray-200">
          CSS Variables
        </h2>
        <p className="cr-text-sm cr-text-gray-600 cr-mb-4">
          CSS custom properties available globally via <code className="cr-bg-gray-100 cr-px-1 cr-rounded">var(--name)</code>.
        </p>
        <div className="cr-bg-white cr-rounded-lg cr-border cr-border-gray-200 cr-p-4">
          <pre className="cr-text-sm cr-font-mono cr-text-gray-700 cr-overflow-x-auto">
{`:root {
  --cirrus-blue-10: #E8F8FE;
  --cirrus-blue-25: #DEF1F9;
  --cirrus-blue-35: #CCDDE5;
  --cirrus-blue-50: #CAE2EA;
  --cirrus-blue-75: #BBD5DE;
  --cirrus-blue-100: #B0C9D1;
  --cirrus-blue-400: #03365D;
}`}
          </pre>
        </div>
      </section>
    </div>
  );
};

// =============================================================================
// Typography Tab
// =============================================================================
const TypographyTab: React.FC = () => {
  const fontFamilies = getFontFamilies() as Record<string, string>;
  const screens = getScreens() as Record<string, string>;

  return (
    <div className="cr-space-y-8">
      <section>
        <h2 className="cr-text-xl cr-font-bold cr-text-gray-900 cr-mb-4 cr-pb-2 cr-border-b-2 cr-border-gray-200">
          Font Families
        </h2>
        <p className="cr-text-sm cr-text-gray-600 cr-mb-6">
          Use via Tailwind: <code className="cr-bg-gray-100 cr-px-1 cr-rounded">cr-font-main</code>, <code className="cr-bg-gray-100 cr-px-1 cr-rounded">cr-font-header</code>, etc.
        </p>
        <div className="cr-space-y-6">
          {Object.entries(fontFamilies).map(([name, family]) => (
            <div key={name} className="cr-bg-white cr-rounded-lg cr-border cr-border-gray-200 cr-p-6">
              <div className="cr-flex cr-justify-between cr-items-start cr-mb-4">
                <div>
                  <h3 className="cr-font-semibold cr-text-gray-900">font-{name}</h3>
                  <p className="cr-text-sm cr-font-mono cr-text-gray-600">{family as string}</p>
                </div>
                <code className="cr-bg-gray-100 cr-px-2 cr-py-1 cr-rounded cr-text-xs">cr-font-{name}</code>
              </div>
              <p className="cr-text-2xl" style={{ fontFamily: family }}>
                The quick brown fox jumps over the lazy dog
              </p>
              <p className="cr-text-lg cr-mt-2" style={{ fontFamily: family as string }}>
                ABCDEFGHIJKLMNOPQRSTUVWXYZ abcdefghijklmnopqrstuvwxyz 0123456789
              </p>
            </div>
          ))}
        </div>
      </section>

      <section>
        <h2 className="cr-text-xl cr-font-bold cr-text-gray-900 cr-mb-4 cr-pb-2 cr-border-b-2 cr-border-gray-200">
          SCSS Font Classes
        </h2>
        <p className="cr-text-sm cr-text-gray-600 cr-mb-6">
          Legacy CSS classes from <code className="cr-bg-gray-100 cr-px-1 cr-rounded">index.scss</code>.
        </p>
        <div className="cr-space-y-4">
          <div className="cr-bg-white cr-rounded-lg cr-border cr-border-gray-200 cr-p-6">
            <div className="cr-flex cr-justify-between cr-items-center cr-mb-3">
              <code className="cr-bg-gray-100 cr-px-2 cr-py-1 cr-rounded cr-text-sm">.font-head</code>
              <span className="cr-text-sm cr-text-gray-600">Poppins, 700</span>
            </div>
            <p className="font-head cr-text-2xl">The quick brown fox jumps over the lazy dog</p>
          </div>
          <div className="cr-bg-white cr-rounded-lg cr-border cr-border-gray-200 cr-p-6">
            <div className="cr-flex cr-justify-between cr-items-center cr-mb-3">
              <code className="cr-bg-gray-100 cr-px-2 cr-py-1 cr-rounded cr-text-sm">.font-forma</code>
              <span className="cr-text-sm cr-text-gray-600">Zen Dots, 400</span>
            </div>
            <p className="font-forma cr-text-2xl">The quick brown fox jumps over the lazy dog</p>
          </div>
          <div className="cr-bg-white cr-rounded-lg cr-border cr-border-gray-200 cr-p-6">
            <div className="cr-flex cr-justify-between cr-items-center cr-mb-3">
              <code className="cr-bg-gray-100 cr-px-2 cr-py-1 cr-rounded cr-text-sm">.font-label</code>
              <span className="cr-text-sm cr-text-gray-600">Montserrat, 500</span>
            </div>
            <p className="font-label cr-text-2xl">The quick brown fox jumps over the lazy dog</p>
          </div>
          <div className="cr-bg-white cr-rounded-lg cr-border cr-border-gray-200 cr-p-6">
            <div className="cr-flex cr-justify-between cr-items-center cr-mb-3">
              <code className="cr-bg-gray-100 cr-px-2 cr-py-1 cr-rounded cr-text-sm">.font-montserrat</code>
              <span className="cr-text-sm cr-text-gray-600">Montserrat, 700</span>
            </div>
            <p className="font-montserrat cr-text-2xl">The quick brown fox jumps over the lazy dog</p>
          </div>
        </div>
      </section>

      <section>
        <h2 className="cr-text-xl cr-font-bold cr-text-gray-900 cr-mb-4 cr-pb-2 cr-border-b-2 cr-border-gray-200">
          Type Scale
        </h2>
        <div className="cr-bg-white cr-rounded-lg cr-border cr-border-gray-200 cr-p-6 cr-space-y-4">
          <div className="cr-flex cr-items-baseline cr-gap-4">
            <code className="cr-text-xs cr-text-gray-500 cr-w-24">cr-text-xs</code>
            <span className="cr-text-xs">The quick brown fox (0.75rem)</span>
          </div>
          <div className="cr-flex cr-items-baseline cr-gap-4">
            <code className="cr-text-xs cr-text-gray-500 cr-w-24">cr-text-sm</code>
            <span className="cr-text-sm">The quick brown fox (0.875rem)</span>
          </div>
          <div className="cr-flex cr-items-baseline cr-gap-4">
            <code className="cr-text-xs cr-text-gray-500 cr-w-24">cr-text-base</code>
            <span className="cr-text-base">The quick brown fox (1rem)</span>
          </div>
          <div className="cr-flex cr-items-baseline cr-gap-4">
            <code className="cr-text-xs cr-text-gray-500 cr-w-24">cr-text-lg</code>
            <span className="cr-text-lg">The quick brown fox (1.125rem)</span>
          </div>
          <div className="cr-flex cr-items-baseline cr-gap-4">
            <code className="cr-text-xs cr-text-gray-500 cr-w-24">cr-text-xl</code>
            <span className="cr-text-xl">The quick brown fox (1.25rem)</span>
          </div>
          <div className="cr-flex cr-items-baseline cr-gap-4">
            <code className="cr-text-xs cr-text-gray-500 cr-w-24">cr-text-2xl</code>
            <span className="cr-text-2xl">The quick brown fox (1.5rem)</span>
          </div>
          <div className="cr-flex cr-items-baseline cr-gap-4">
            <code className="cr-text-xs cr-text-gray-500 cr-w-24">cr-text-3xl</code>
            <span className="cr-text-3xl">The quick brown fox (1.875rem)</span>
          </div>
          <div className="cr-flex cr-items-baseline cr-gap-4">
            <code className="cr-text-xs cr-text-gray-500 cr-w-24">cr-text-4xl</code>
            <span className="cr-text-4xl">The quick brown fox (2.25rem)</span>
          </div>
        </div>
      </section>

      <section>
        <h2 className="cr-text-xl cr-font-bold cr-text-gray-900 cr-mb-4 cr-pb-2 cr-border-b-2 cr-border-gray-200">
          Breakpoints
        </h2>
        <div className="cr-bg-white cr-rounded-lg cr-border cr-border-gray-200 cr-overflow-hidden">
          <table className="cr-w-full">
            <thead className="cr-bg-gray-50">
              <tr>
                <th className="cr-text-left cr-px-4 cr-py-3 cr-text-sm cr-font-semibold cr-text-gray-900">Prefix</th>
                <th className="cr-text-left cr-px-4 cr-py-3 cr-text-sm cr-font-semibold cr-text-gray-900">Min Width</th>
                <th className="cr-text-left cr-px-4 cr-py-3 cr-text-sm cr-font-semibold cr-text-gray-900">CSS</th>
              </tr>
            </thead>
            <tbody className="cr-divide-y cr-divide-gray-200">
              {Object.entries(screens).map(([name, width]) => (
                <tr key={name}>
                  <td className="cr-px-4 cr-py-3 cr-font-mono cr-text-sm">{name}:</td>
                  <td className="cr-px-4 cr-py-3 cr-text-sm">{width as string}</td>
                  <td className="cr-px-4 cr-py-3 cr-font-mono cr-text-sm cr-text-gray-600">@media (min-width: {width as string})</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
};

// =============================================================================
// Buttons Tab
// =============================================================================
const ButtonsTab: React.FC = () => {
  const [chipSelected, setChipSelected] = useState<Record<string, boolean>>({
    option1: true,
    option2: false,
    option3: true,
  });
  const [tabActive, setTabActive] = useState<'tab1' | 'tab2' | 'tab3'>('tab1');

  const chipStyle = 'cr-flex cr-items-center cr-justify-center cr-text-center';
  const activeChipStyle = 'chip-active';
  const inactiveChipStyle = 'chip-inactive';

  return (
    <div className="cr-space-y-8">
      {/* Button Component */}
      <section>
        <h2 className="cr-text-xl cr-font-bold cr-text-gray-900 cr-mb-4 cr-pb-2 cr-border-b-2 cr-border-gray-200">
          Button Component
        </h2>
        <p className="cr-text-sm cr-text-gray-600 cr-mb-6">
          Primary button from <code className="cr-bg-gray-100 cr-px-1 cr-rounded">components/Button.tsx</code>
        </p>

        <div className="cr-bg-white cr-rounded-lg cr-border cr-border-gray-200 cr-p-6">
          <div className="cr-flex cr-flex-wrap cr-gap-4 cr-items-center cr-mb-4">
            <button className="cr-flex cr-items-center cr-gap-1 cr-rounded-md cr-border-2 cr-border-solid cr-border-cirrusBlue-100 cr-bg-cirrusBlue-25 cr-px-4 cr-py-2 cr-uppercase cr-text-blue-950 cr-transition-colors cr-duration-200 hover:cr-bg-cirrusBlue-50 hover:cr-text-blue-700">
              Default
            </button>
            <button className="cr-flex cr-items-center cr-gap-1 cr-rounded-md cr-border-2 cr-border-solid cr-border-cirrusBlue-100 cr-bg-cirrusBlue-100 cr-px-4 cr-py-2 cr-uppercase cr-text-blue-950 cr-opacity-50" disabled>
              Disabled
            </button>
          </div>
          <pre className="cr-bg-gray-900 cr-text-gray-100 cr-p-4 cr-rounded-lg cr-text-sm cr-overflow-x-auto">
{`// Button.tsx
cr-border-2 cr-border-solid cr-border-cirrusBlue-100
cr-bg-cirrusBlue-25 cr-text-blue-950
cr-transition-colors cr-duration-200
hover:cr-bg-cirrusBlue-50 hover:cr-text-blue-700
disabled:cr-bg-cirrusBlue-100 disabled:cr-opacity-50`}
          </pre>
        </div>
      </section>

      {/* Tab Menu */}
      <section>
        <h2 className="cr-text-xl cr-font-bold cr-text-gray-900 cr-mb-4 cr-pb-2 cr-border-b-2 cr-border-gray-200">
          Tab Menu (Toggle Buttons)
        </h2>
        <p className="cr-text-sm cr-text-gray-600 cr-mb-6">
          Navigation tabs from <code className="cr-bg-gray-100 cr-px-1 cr-rounded">components/TabMenu.tsx</code>.
          Active = navy background, Inactive = light blue background with navy text.
        </p>

        <div className="cr-bg-white cr-rounded-lg cr-border cr-border-gray-200 cr-p-6">
          <div className="cr-flex cr-flex-wrap cr-gap-2 cr-mb-4">
            {(['tab1', 'tab2', 'tab3'] as const).map((tab) => (
              <button
                key={tab}
                onClick={() => setTabActive(tab)}
                className={`cr-flex cr-items-center cr-justify-center cr-gap-x-2 cr-rounded-md cr-px-4 cr-py-2.5
                  cr-text-base cr-font-semibold cr-no-underline cr-transition-colors cr-duration-200 ${
                  tabActive === tab
                    ? 'cr-bg-blue-900 cr-text-white'
                    : 'cr-bg-cirrusBlue-100 cr-text-blue-900 hover:cr-bg-gray-350'
                }`}
              >
                {tab === 'tab1' ? 'Active Tab' : tab === 'tab2' ? 'Inactive Tab' : 'Another Tab'}
              </button>
            ))}
          </div>
          <pre className="cr-bg-gray-900 cr-text-gray-100 cr-p-4 cr-rounded-lg cr-text-sm cr-overflow-x-auto">
{`// TabMenu.tsx
// Active state:
cr-bg-blue-900 cr-text-white

// Inactive state:
cr-bg-cirrusBlue-100 cr-text-blue-900 hover:cr-bg-gray-350`}
          </pre>
        </div>
      </section>

      {/* Chip Selector */}
      <section>
        <h2 className="cr-text-xl cr-font-bold cr-text-gray-900 cr-mb-4 cr-pb-2 cr-border-b-2 cr-border-gray-200">
          Chip Selector (Navy Border when Unselected)
        </h2>
        <p className="cr-text-sm cr-text-gray-600 cr-mb-6">
          Toggle chips from <code className="cr-bg-gray-100 cr-px-1 cr-rounded">components/ChipSelector.tsx</code>.
          Unselected = navy border + gray background. Selected = light blue background.
        </p>

        <div className="cr-bg-white cr-rounded-lg cr-border cr-border-gray-200 cr-p-6">
          <div className="cr-flex cr-flex-wrap cr-gap-2 cr-mb-4">
            {Object.entries(chipSelected).map(([key, isSelected]) => (
              <div
                key={key}
                onClick={() => setChipSelected((prev) => ({ ...prev, [key]: !prev[key] }))}
                className={`${chipStyle} ${isSelected ? activeChipStyle : inactiveChipStyle}`}
              >
                {isSelected ? 'Selected' : 'Unselected'}
              </div>
            ))}
          </div>
          <pre className="cr-bg-gray-900 cr-text-gray-100 cr-p-4 cr-rounded-lg cr-text-sm cr-overflow-x-auto">
{`// index.scss - Chip CSS Classes

.chip-active {
  background-color: #CCDDE5; /* cirrusBlue-50 */
  color: #03365D;
}
.chip-active:hover {
  background-color: #B0C9D1; /* cirrusBlue-100 */
}

.chip-inactive {
  background-color: #f3f4f6; /* gray-100 */
  color: #03365D;
  border: 2px solid #03365D;
}
.chip-inactive:hover {
  background-color: #e5e7eb; /* gray-200 */
}`}
          </pre>
        </div>
      </section>

      {/* CSS Button Classes */}
      <section>
        <h2 className="cr-text-xl cr-font-bold cr-text-gray-900 cr-mb-4 cr-pb-2 cr-border-b-2 cr-border-gray-200">
          CSS Button Classes
        </h2>
        <p className="cr-text-sm cr-text-gray-600 cr-mb-6">
          Global button classes defined in <code className="cr-bg-gray-100 cr-px-1 cr-rounded">index.scss</code>.
        </p>

        <div className="cr-space-y-6">
          <div className="cr-bg-white cr-rounded-lg cr-border cr-border-gray-200 cr-p-6">
            <div className="cr-flex cr-justify-between cr-items-center cr-mb-4">
              <h3 className="cr-font-semibold cr-text-gray-900">.main-button</h3>
              <code className="cr-bg-gray-100 cr-px-2 cr-py-1 cr-rounded cr-text-xs">className=&quot;main-button&quot;</code>
            </div>
            <div className="cr-flex cr-flex-wrap cr-gap-4 cr-items-center cr-mb-4">
              <button className="main-button">Default</button>
              <button className="main-button active">Active</button>
              <button className="main-button" disabled>Disabled</button>
            </div>
            <pre className="cr-bg-gray-900 cr-text-gray-100 cr-p-4 cr-rounded-lg cr-text-sm cr-overflow-x-auto">
{`.main-button {
  background-color: #B0C9D1;
  color: #03365D (70% opacity);
}
.main-button:hover { background-color: #9BB8C2; }
.main-button.active { color: #03365D (full); }`}
            </pre>
          </div>

          <div className="cr-bg-white cr-rounded-lg cr-border cr-border-gray-200 cr-p-6">
            <div className="cr-flex cr-justify-between cr-items-center cr-mb-4">
              <h3 className="cr-font-semibold cr-text-gray-900">.secondary-button</h3>
              <code className="cr-bg-gray-100 cr-px-2 cr-py-1 cr-rounded cr-text-xs">className=&quot;secondary-button&quot;</code>
            </div>
            <div className="cr-flex cr-flex-wrap cr-gap-4 cr-items-center cr-mb-4">
              <button className="secondary-button">Secondary</button>
            </div>
            <pre className="cr-bg-gray-900 cr-text-gray-100 cr-p-4 cr-rounded-lg cr-text-sm cr-overflow-x-auto">
{`.secondary-button {
  background-color: #B0C9D1;
  color: #03365D;
  border: 2px solid #B0C9D1;
}
.secondary-button:hover {
  background-color: #d3d3d3;
  border-color: #d3d3d3;
}`}
            </pre>
          </div>

          <div className="cr-bg-white cr-rounded-lg cr-border cr-border-gray-200 cr-p-6">
            <div className="cr-flex cr-justify-between cr-items-center cr-mb-4">
              <h3 className="cr-font-semibold cr-text-gray-900">.chip-active / .chip-inactive</h3>
              <code className="cr-bg-gray-100 cr-px-2 cr-py-1 cr-rounded cr-text-xs">className=&quot;chip-active&quot; / &quot;chip-inactive&quot;</code>
            </div>
            <div className="cr-flex cr-flex-wrap cr-gap-4 cr-items-center cr-mb-4">
              <div className="chip-active cr-flex cr-items-center cr-justify-center">Active Chip</div>
              <div className="chip-inactive cr-flex cr-items-center cr-justify-center">Inactive Chip</div>
            </div>
            <pre className="cr-bg-gray-900 cr-text-gray-100 cr-p-4 cr-rounded-lg cr-text-sm cr-overflow-x-auto">
{`.chip-active {
  background-color: #CCDDE5; /* cirrusBlue-50 */
  color: #03365D;
}
.chip-active:hover {
  background-color: #B0C9D1; /* cirrusBlue-100 */
}

.chip-inactive {
  background-color: #f3f4f6; /* gray-100 */
  color: #03365D;
  border: 2px solid #03365D;
}
.chip-inactive:hover {
  background-color: #e5e7eb; /* gray-200 */
}`}
            </pre>
          </div>
        </div>
      </section>

      {/* Color Reference */}
      <section>
        <h2 className="cr-text-xl cr-font-bold cr-text-gray-900 cr-mb-4 cr-pb-2 cr-border-b-2 cr-border-gray-200">
          Button Color Reference
        </h2>
        <div className="cr-bg-white cr-rounded-lg cr-border cr-border-gray-200 cr-p-6">
          <div className="cr-grid cr-grid-cols-2 md:cr-grid-cols-4 cr-gap-4">
            <div className="cr-text-center">
              <div className="cr-w-full cr-h-12 cr-rounded-md cr-mb-2" style={{ backgroundColor: '#03365D' }} />
              <p className="cr-text-xs cr-font-semibold">Navy Blue</p>
              <p className="cr-text-xs cr-font-mono cr-text-gray-600">#03365D</p>
              <p className="cr-text-2xs cr-text-gray-500">Active bg, borders</p>
            </div>
            <div className="cr-text-center">
              <div className="cr-w-full cr-h-12 cr-rounded-md cr-mb-2" style={{ backgroundColor: '#B0C9D1' }} />
              <p className="cr-text-xs cr-font-semibold">Cirrus Blue 100</p>
              <p className="cr-text-xs cr-font-mono cr-text-gray-600">#B0C9D1</p>
              <p className="cr-text-2xs cr-text-gray-500">Inactive bg</p>
            </div>
            <div className="cr-text-center">
              <div className="cr-w-full cr-h-12 cr-rounded-md cr-mb-2" style={{ backgroundColor: '#CAE2EA' }} />
              <p className="cr-text-xs cr-font-semibold">Cirrus Blue 50</p>
              <p className="cr-text-xs cr-font-mono cr-text-gray-600">#CAE2EA</p>
              <p className="cr-text-2xs cr-text-gray-500">Selected chip bg</p>
            </div>
            <div className="cr-text-center">
              <div className="cr-w-full cr-h-12 cr-rounded-md cr-mb-2" style={{ backgroundColor: '#DEF1F9' }} />
              <p className="cr-text-xs cr-font-semibold">Cirrus Blue 25</p>
              <p className="cr-text-xs cr-font-mono cr-text-gray-600">#DEF1F9</p>
              <p className="cr-text-2xs cr-text-gray-500">Button.tsx bg</p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

// =============================================================================
// Forms Tab — live demos of the reusable form & button components
// (folded in from the former standalone StyleGuide page).
// =============================================================================
interface FormsDemoData {
  exampleInput: string;
}

const FormsTab: React.FC = () => {
  const formMethods = useForm<FormsDemoData>({ defaultValues: { exampleInput: '' } });

  return (
    <FormProvider {...formMethods}>
      <div className="cr-space-y-8">
        <section>
          <h2 className="cr-text-xl cr-font-bold cr-text-gray-900 cr-mb-4 cr-pb-2 cr-border-b-2 cr-border-gray-200">
            Button Components
          </h2>
          <p className="cr-text-sm cr-text-gray-600 cr-mb-6">
            Live <code className="cr-bg-gray-100 cr-px-1 cr-rounded">components/Button.tsx</code> and{' '}
            <code className="cr-bg-gray-100 cr-px-1 cr-rounded">components/ButtonList.tsx</code> — the actual
            reusable components (the Buttons tab documents the raw CSS classes).
          </p>
          <div className="cr-bg-white cr-rounded-lg cr-border cr-border-gray-200 cr-p-6 cr-space-y-4">
            <div>
              <h3 className="cr-mb-2 cr-text-lg cr-font-medium">Primary Buttons</h3>
              <ButtonList>
                <Button
                  onClick={() => {
                    console.error('Primary Button not implemented');
                  }}
                  icon={<HiOutlineCheckCircle className="cr-size-7" />}
                >
                  Primary Button
                </Button>
                <Button
                  onClick={() => {
                    console.error('Secondary Button not implemented');
                  }}
                  icon={<HiOutlineXMark className="cr-size-7" />}
                >
                  Secondary Button
                </Button>
                <Button
                  onClick={() => {
                    console.error('Outline Button not implemented');
                  }}
                  icon={<HiOutlineInformationCircle className="cr-size-7" />}
                >
                  Outline Button
                </Button>
              </ButtonList>
            </div>
            <div>
              <h3 className="cr-mb-2 cr-text-lg cr-font-medium">Button States</h3>
              <ButtonList>
                <Button
                  onClick={() => {
                    console.error('Loading State not implemented');
                  }}
                  className="cr-opacity-50"
                >
                  Loading State
                </Button>
              </ButtonList>
            </div>
          </div>
        </section>

        <section>
          <h2 className="cr-text-xl cr-font-bold cr-text-gray-900 cr-mb-4 cr-pb-2 cr-border-b-2 cr-border-gray-200">
            Form Elements
          </h2>
          <p className="cr-text-sm cr-text-gray-600 cr-mb-6">
            Live <code className="cr-bg-gray-100 cr-px-1 cr-rounded">components/FormEntry.tsx</code> and{' '}
            <code className="cr-bg-gray-100 cr-px-1 cr-rounded">components/FormInput.tsx</code>, wired through
            react-hook-form.
          </p>
          <div className="cr-bg-white cr-rounded-lg cr-border cr-border-gray-200 cr-p-6 cr-space-y-4">
            <FormEntry label="Text Input">
              <FormInput fieldName="exampleInput" inputType="text" customClass="cr-w-full" />
            </FormEntry>
            <FormEntry label="Email Input">
              <FormInput fieldName="exampleInput" inputType="email" customClass="cr-w-full" />
            </FormEntry>
          </div>
        </section>
      </div>
    </FormProvider>
  );
};

// =============================================================================
// Main Component
// =============================================================================
const DesignSystem: React.FC = () => {
  const [activeTab, setActiveTab] = useState<TabType>('icons');

  const renderTabContent = () => {
    switch (activeTab) {
      case 'icons':
        return <IconsTab />;
      case 'colors':
        return <ColorsTab />;
      case 'typography':
        return <TypographyTab />;
      case 'buttons':
        return <ButtonsTab />;
      case 'forms':
        return <FormsTab />;
      default:
        return <IconsTab />;
    }
  };

  return (
    <div className="cr-min-h-screen cr-bg-gray-50">
      <div className="cr-max-w-7xl cr-mx-auto cr-px-4 sm:cr-px-6 lg:cr-px-8 cr-py-8">
        <header className="cr-mb-8">
          <h1 className="cr-text-3xl cr-font-bold cr-text-gray-900 cr-mb-2">Design System</h1>
          <p className="cr-text-gray-600">Visual reference for icons, colors, typography, and UI components</p>
        </header>

        <div className="cr-bg-white cr-rounded-lg cr-shadow-sm cr-border cr-border-gray-200 cr-overflow-hidden">
          <nav className="cr-flex cr-border-b cr-border-gray-200 cr-bg-gray-50">
            <TabButton active={activeTab === 'icons'} onClick={() => setActiveTab('icons')}>
              Icons
            </TabButton>
            <TabButton active={activeTab === 'colors'} onClick={() => setActiveTab('colors')}>
              Colors
            </TabButton>
            <TabButton active={activeTab === 'typography'} onClick={() => setActiveTab('typography')}>
              Typography
            </TabButton>
            <TabButton active={activeTab === 'buttons'} onClick={() => setActiveTab('buttons')}>
              Buttons
            </TabButton>
            <TabButton active={activeTab === 'forms'} onClick={() => setActiveTab('forms')}>
              Forms
            </TabButton>
          </nav>

          <div className="cr-p-6">
            {renderTabContent()}
          </div>
        </div>
      </div>
    </div>
  );
};

export default DesignSystem;
